/**
 * Маршрут внутри объёма (S9): «Маршрут» в карточке арендатора строит путь прямо
 * по этажу, не выходя из 3D. Стартом берём вход этажа — лестницу, эскалатор,
 * лифт или дверь; если ничего этого на этаже нет, идём от края этажа, ближайшего
 * к цели (честно: точной позиции человека внутри здания у нас нет).
 */
import { tr } from '@app/i18n'
import { useCallback, useEffect, useState } from 'preact/hooks'
import { indoorPath, pathLengthM } from '../volume3d/indoor-path'
import type { MPoint, VolumeModel } from '../volume3d/model'
import type { VolumeScene } from '../volume3d/scene'

/** Категории, от которых логично начинать путь по этажу. */
const ENTRY_CATEGORIES = new Set(['entrance', 'escalator', 'elevator', 'stairs', 'info'])

/** Точка старта на этаже: вход/подъём, иначе — край этажа со стороны цели. */
function entryPoint(floorModel: VolumeModel['floors'][number], target: MPoint): MPoint {
  const entries = floorModel.tenants.filter((t) => ENTRY_CATEGORIES.has(t.category))
  if (entries.length > 0) {
    let best = entries[0] as (typeof entries)[number]
    let bestD = Number.POSITIVE_INFINITY
    for (const e of entries) {
      const d = Math.hypot(e.at[0] - target[0], e.at[1] - target[1])
      if (d < bestD) {
        bestD = d
        best = e
      }
    }
    return best.at
  }
  let far: MPoint = floorModel.outline[0] ?? [0, 0]
  let farD = -1
  for (const p of floorModel.outline) {
    const d = Math.hypot(p[0] - target[0], p[1] - target[1])
    if (d > farD) {
      farD = d
      far = p
    }
  }
  return far
}

export interface IndoorRoute {
  /** Точки последнего построенного пути — для проверки в тестах и смоуке. */
  readonly points: readonly [number, number][] | null
  /** Можно ли построить путь прямо здесь (цель на открытом этаже). */
  readonly can: boolean
  /** Построить путь. */
  readonly run: () => void
  /** Подсказка под сценой: длина пути или «пройти нельзя». */
  readonly hint: string
}

export function useIndoorRoute(
  model: VolumeModel | null,
  floor: number | null,
  tenantId: string | null,
  sceneRef: { current: VolumeScene | null },
): IndoorRoute {
  const [hint, setHint] = useState('')
  const [points, setPoints] = useState<readonly [number, number][] | null>(null)
  const floorModel = model && floor !== null ? model.floors[floor - 1] : undefined
  const zone = floorModel?.zones.find((z) => z.tenantId === tenantId)

  // Смена этажа или выбор ДРУГОГО арендатора — старый путь убираем. Закрытие
  // карточки (tenantId → null) путь не трогает: её как раз и закрывают, чтобы
  // посмотреть на построенную линию.
  useEffect(() => {
    if (tenantId === null) return
    sceneRef.current?.setPath(null, floor ?? 1)
    setPoints(null)
    setHint('')
  }, [floor, tenantId, sceneRef])

  const run = useCallback(() => {
    if (!floorModel || !zone || floor === null) return
    const from = entryPoint(floorModel, zone.center)
    const path = indoorPath(floorModel.outline, floorModel.zones, from, zone.tenantId)
    if (!path) {
      sceneRef.current?.setPath(null, floor)
      setPoints(null)
      setHint(tr('volume3d.route_none'))
      return
    }
    const line = path.map((p) => [p[0], p[1]] as [number, number])
    sceneRef.current?.setPath(line, floor)
    setPoints(line)
    setHint(tr('volume3d.route_len', { m: String(Math.round(pathLengthM(path))) }))
  }, [floorModel, zone, floor, sceneRef])

  return { can: Boolean(zone), run, hint, points }
}
